<template>
    <section>
        <div class="access-privileges-header">
            <h3>
                {{ AccessPrivilegesConstants.TAB_HEADER }}
            </h3>
        </div>

        <div data-qa="user-sharing" class="contact-access-privileges">
            <h3>
                {{ AccessPrivilegesConstants.USER_SHARING }}
            </h3>

            <ds-outline-button
                with-icon
                data-qa="add-user-privileges"
                @click="addUserPrivileges"
            >
                <ds-icon name="add" />

                <div> {{ AccessPrivilegesConstants.ADD_USER_BUTTON }} </div>
            </ds-outline-button>
        </div>

        <div>
            <ds-table-grid
                v-if="isContactHasSharedUsers"
                data-qa="shared-users-table"
                :data="sharedUsers"
                class="access-privileges-table"
            >
                <ds-table-grid-column
                    label="User name"
                    prop="userName"
                    width="30"
                />

                <ds-table-grid-column
                    label="Id"
                    prop="userId"
                    width="20"
                />

                <ds-table-grid-column
                    label="Permissions"
                    prop="shareLevel"
                    width="25"
                >
                    <template slot-scope="sharedUser">
                        <span> {{ shareLevel(sharedUser) }} </span>
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    label="Remove"
                    align="center"
                    width="25"
                >
                    <template slot-scope="sharedUser">
                        <div @click="removeSharedUser(sharedUser)">
                            <img
                                src="/resources/funnel/images/svg/minus.svg"
                                onmouseover="this.src='/resources/funnel/images/svg/click.svg'"
                                onmouseout="this.src='/resources/funnel/images/svg/minus.svg'"
                                class="remove-button"
                            />
                        </div>
                    </template>
                </ds-table-grid-column>
            </ds-table-grid>

            <div v-else class="empty-table">
                <p class="empty-table-label">
                    {{ AccessPrivilegesConstants.USER_EMPTY_TABLE_LABEL }}
                </p>
            </div>
        </div>

        <div data-qa="group-sharing" class="contact-access-privileges">
            <h3>
                {{ AccessPrivilegesConstants.GROUP_SHARING }}
            </h3>

            <ds-outline-button
                with-icon
                data-qa="add-group-privileges"
                @click="addGroupPrivileges"
            >
                <ds-icon name="add" />

                <div> {{ AccessPrivilegesConstants.ADD_GROUP_BUTTON }} </div>
            </ds-outline-button>
        </div>

        <div>
            <ds-table-grid
                v-if="isContactHasSharedGroups"
                data-qa="shared-user-groups-table"
                :data="sharedUserGroups"
                class="access-privileges-table"
            >
                <ds-table-grid-column
                    label="Group name"
                    prop="groupName"
                    width="30"
                />

                <ds-table-grid-column
                    label="Id"
                    prop="groupId"
                    width="20"
                />

                <ds-table-grid-column
                    label="Permissions"
                    prop="shareLevel"
                    width="25"
                >
                    <template slot-scope="sharedUserGroup">
                        <span> {{ shareLevel(sharedUserGroup) }} </span>
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    label="Remove"
                    align="center"
                    width="25"
                >
                    <template slot-scope="sharedUserGroup">
                        <div @click="removeSharedGroup(sharedUserGroup)">
                            <img
                                src="/resources/funnel/images/svg/minus.svg"
                                onmouseover="this.src='/resources/funnel/images/svg/click.svg'"
                                onmouseout="this.src='/resources/funnel/images/svg/minus.svg'"
                                class="remove-button"
                            />
                        </div>
                    </template>
                </ds-table-grid-column>
            </ds-table-grid>

            <div v-else class="empty-table">
                <p class="empty-table-label">
                    {{ AccessPrivilegesConstants.GROUP_EMPTY_TABLE_LABEL }}
                </p>
            </div>
        </div>

        <contact-access-privileges-modal
            ref="privilegesModal"
            :user-or-group-options="privilegesModalOptions"
            :modal-content="modalContent"
            :add-privilege="addUserOrGroupPrivilege"
        />
    </section>
</template>

<script>
import axios from 'axios';
import ContactAccessPrivilegesModal from './ContactAccessPrivilegesModal';
import AccessPrivilegesConstants from './ContactAccessPrivilegesConstants';

export default {
    components: {
        ContactAccessPrivilegesModal,
    },

    props: {
        contact: Object,
    },

    data() {
        return {
            usersList: [],
            groupList: [],
            sharedUsers: [],
            sharedUserGroups: [],
            privilegesModalOptions: [],
            modalContent: {},
            AccessPrivilegesConstants,
            addUserOrGroupPrivilege: Function,
        };
    },

    created() {
        this.loadUsers();
    },

    computed: {
        isContactHasSharedUsers() {
            return this.sharedUsers?.length > 0;
        },

        isContactHasSharedGroups() {
            return this.sharedUserGroups?.length > 0;
        },
    },

    methods: {
        loadUsers() {
            axios.get(`/app/accessPrivileges/getUsersAndGroups/${this.contact.id}`).then((response) => {
                const users = [];
                const userGroups = [];

                Object.entries(response.data.users).map((entry) => {
                    return users.push({ value: Number(entry[0]), label: entry[1] });
                });

                Object.entries(response.data.userGroups).map((entry) => {
                    return userGroups.push({ value: Number(entry[0]), label: entry[1] });
                });

                this.usersList = users;
                this.groupList = userGroups;
                this.sharedUsers = response.data.sharedUsers;
                this.sharedUserGroups = response.data.sharedUserGroups;
            });
        },

        shareLevel(sharedUser) {
            return sharedUser?.shareLevel === '2' ? this.AccessPrivilegesConstants.WRITE_PERMISSION : this.AccessPrivilegesConstants.READ_PERMISSION;
        },

        addUserPrivileges() {
            this.privilegesModalOptions = this.usersList;
            this.modalContent = this.AccessPrivilegesConstants.ADD_USER_PRIVILEGES_MODAL;
            this.addUserOrGroupPrivilege = this.addSharedUser;
            this.$refs.privilegesModal.open();
        },

        addGroupPrivileges() {
            this.privilegesModalOptions = this.groupList;
            this.modalContent = this.AccessPrivilegesConstants.ADD_GROUP_PRIVILEGES_MODAL;
            this.addUserOrGroupPrivilege = this.addSharedGroup;
            this.$refs.privilegesModal.open();
        },

        async addSharedUser(selectedUserId, selectedUserMode) {
            axios.put('/app/accessPrivileges/addSharedUser', {
                userId: selectedUserId,
                shareMode: selectedUserMode,
                contactId: this.contact.id,
            }).then(async () => {
                await this.loadUsers();
            });
        },

        async addSharedGroup(selectedGroupId, selectedGroupMode) {
            axios.put('/app/accessPrivileges/addSharedUserGroup', {
                groupId: selectedGroupId,
                shareMode: selectedGroupMode,
                contactId: this.contact.id,
            }).then(async () => {
                await this.loadUsers();
            });
        },

        async removeSharedUser(sharedUser) {
            axios.delete(`/app/accessPrivileges/deleteSharedUser/${sharedUser.id}`).then(async () => {
                this.$toast({ message: 'User permission removed' });
                await this.loadUsers();
            });
        },

        async removeSharedGroup(sharedGroup) {
            axios.delete(`/app/accessPrivileges/deleteSharedUserGroup/${sharedGroup.id}`).then(async () => {
                this.$toast({ message: 'Group permission removed' });
                await this.loadUsers();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.access-privileges-header {
    border-bottom: 1px solid $color-gray-lighter;
    padding: $spacing-400 0;
}

.contact-access-privileges {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: $spacing-200;
}

.empty-table {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    border-bottom: 1px solid $color-gray-lighter;
    margin-top: $spacing-200;
}

.empty-table-label {
    color: $color-gray-darker;
    font-size: $font-size-xl;
    font-weight: $font-weight-headlines;
    padding-top: $spacing-800;
    padding-bottom: $spacing-600;
}

.remove-button {
    cursor: pointer;
}

.access-privileges-table {
    margin-top: $spacing-200;
    white-space: nowrap;
}
</style>
